import type { Message, Part, Session } from "@mux/sdk/v2/client"
import { batch } from "solid-js"
import { produce, reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { createTranscriptReplicaRepository, type TranscriptReplicaRepository } from "@/storage/transcript-replica"
import { Binary } from "@/utils/binary"
import { retry } from "@/utils/retry"
import { createSessionAuxiliaryController } from "./session/auxiliary-controller"
import { createSessionCacheLifecycleController } from "./session/cache-lifecycle-controller"
import type { SessionSyncClient } from "./session/contracts"
import { createSessionModelUpdater } from "./session/model-updater"
import { applyOptimisticAdd, applyOptimisticRemove } from "./session/optimistic"
import { createSessionOptimisticController } from "./session/optimistic-controller"
import { createProvisionalSessionController } from "./session/provisional-controller"
import { createSessionTranscriptController } from "./session/transcript-controller"
import { removeMessageData } from "./message-event-reducer"
import { dropSessionCaches } from "./session-cache"
import { createSessionPrefetchStore } from "./session-prefetch"
import { admitsSession, newestSessions, removedRevision } from "./session-revision"
import { sessionNotFoundError } from "./session/message-reducer"
import type { GlobalStore } from "./types"

export {
  isSessionNotFoundError,
  mergeCompletedAssistantParts,
  mergeIncompleteAssistantMessages,
  mergeIncompleteAssistantParts,
  mergeMessageParts,
} from "./session/message-reducer"
export { applyOptimisticAdd, applyOptimisticRemove, mergeOptimisticPage } from "./session/optimistic"

type SessionSyncStore = Store<GlobalStore>
type SessionSyncSetter = SetStoreFunction<GlobalStore>

const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

export function createSessionSync(input: {
  data: SessionSyncStore
  set: SessionSyncSetter
  clientFor: (directory: string) => SessionSyncClient
  cacheServerKey: () => string
  /** Whether the event stream delivers every event of this chat from now on. */
  live: (sessionID: string) => boolean
  /** The last event the stream committed (see the transcript controller's `currentSince`); without one, no transcript is current since a reconnect. */
  streamCursor?: () => string | undefined
  transcriptReplica?: TranscriptReplicaRepository
  onSessionSeed?: (
    directory: string,
    session: Session,
    options?: { isNew?: boolean },
  ) => void
  /** Called when the chats it tracks or mounted surfaces retain change. */
  onTrackedChange?: () => void
}) {
  const provisional = createProvisionalSessionController()
  const prefetch = createSessionPrefetchStore({ scope: input.cacheServerKey })
  const transcriptReplica = input.transcriptReplica ?? createTranscriptReplicaRepository()
  let transcript: ReturnType<typeof createSessionTranscriptController>
  let auxiliary: ReturnType<typeof createSessionAuxiliaryController>

  const getSession = (sessionID: string) => input.data.session.find((session) => session.id === sessionID)
  const optimistic = createSessionOptimisticController({
    addLocal(update) {
      input.set(produce((draft) => applyOptimisticAdd(draft, update)))
    },
    removeLocal(update) {
      input.set(produce((draft) => applyOptimisticRemove(draft, update)))
    },
  })

  const evictData = (directory: string, sessionIDs: string[]) => {
    if (sessionIDs.length === 0) return
    prefetch.clear(directory, sessionIDs)
    optimistic.clear(directory, sessionIDs)
    transcript?.clear(directory, sessionIDs)
    auxiliary?.clear(directory, sessionIDs)
    input.set(
      produce((draft) => {
        // Actors are keyed by message alone; release them while the session's
        // messages still say which ones are its own.
        for (const sessionID of sessionIDs)
          removeMessageData(draft, draft.message[sessionID]?.map((message) => message.id) ?? [])
        dropSessionCaches(draft, sessionIDs)
      }),
    )
  }

  const lifecycle = createSessionCacheLifecycleController({
    scope: input.cacheServerKey,
    size(_directory, sessionID) {
      let parts = 0
      for (const message of input.data.message[sessionID] ?? []) parts += input.data.part[message.id]?.length ?? 0
      return parts
    },
    onEvict: evictData,
    onRelease: (directory, sessionID) => transcript.trimToLatest(directory, sessionID),
    onChange: input.onTrackedChange,
  })

  transcript = createSessionTranscriptController({
    messagesClientFor: input.clientFor,
    infoClientFor: input.clientFor,
    getMessages: (sessionID) => input.data.message[sessionID],
    getParts: (messageID) => input.data.part[messageID],
    getSession,
    findSessionIDForMessage(messageID) {
      for (const [sessionID, messages] of Object.entries(input.data.message)) {
        if (messages?.some((message) => message.id === messageID)) return sessionID
      }
    },
    setMessages(sessionID, messages) {
      input.set("message", sessionID, messages === undefined ? undefined : reconcile(messages, { key: "id" }))
    },
    setMessage(sessionID, index, message) {
      input.set("message", sessionID, index, reconcile(message))
    },
    setParts(messageID, parts) {
      input.set("part", messageID, reconcile(parts, { key: "id" }))
    },
    removeParts(messageIDs) {
      input.set(produce((draft) => removeMessageData(draft, messageIDs)))
    },
    upsertSession(session) {
      input.set(
        "session",
        produce((draft) => {
          const existing = draft.findIndex((item) => item.id === session.id)
          if (existing !== -1) draft[existing] = session
          else {
            const index = draft.findIndex((item) => item.id > session.id)
            draft.splice(index === -1 ? draft.length : index, 0, session)
          }
        }),
      )
    },
    tracked: lifecycle.tracked,
    touch: lifecycle.touch,
    markMissing,
    optimistic,
    prefetch,
    replica: transcriptReplica,
    replicaScope: input.cacheServerKey,
    eventsLive: input.live,
    streamCursor: () => input.streamCursor?.(),
  })

  auxiliary = createSessionAuxiliaryController({
    diffClientFor: input.clientFor,
    tracked: lifecycle.tracked,
    touch: lifecycle.touch,
    getDiff: (sessionID) => input.data.session_diff[sessionID],
    setDiff(sessionID, diff) {
      input.set("session_diff", sessionID, reconcile(diff, { key: "file" }))
    },
    getDiffSummary: (sessionID) => input.data.session_diff_summary[sessionID],
    setDiffSummary(sessionID, summary) {
      input.set("session_diff_summary", sessionID, summary)
    },
  })

  const updateModel = createSessionModelUpdater({
    clientFor: input.clientFor,
    getSession,
    ready: (sessionID) => (provisional.has(sessionID) ? provisional.wait(sessionID) : true),
    updateLocal(sessionID, next, expected) {
      const index = input.data.session.findIndex((item) => item.id === sessionID)
      const item = input.data.session[index]
      if (!item) return
      const current = {
        agent: item.agent ?? "build",
        providerID: item.providerID ?? "",
        modelID: item.modelID ?? "",
        variant: item.variant ?? "",
      }
      if (
        expected &&
        (current.agent !== expected.agent ||
          current.providerID !== expected.providerID ||
          current.modelID !== expected.modelID ||
          current.variant !== expected.variant)
      ) return
      input.set("session", index, { ...item, ...next })
    },
  })

  function markMissing(directory: string, sessionID: string) {
    if (!lifecycle.markMissing(directory, sessionID)) return
    provisional.settle(sessionID, false)
    input.set("session_removed", sessionID, removedRevision(input.data, sessionID, getSession(sessionID)))
    input.set("session", (sessions: Session[]) => sessions.filter((session) => session.id !== sessionID))
    input.set("session_status", sessionID, undefined)
    input.set("session_waiting", sessionID, undefined)
    transcript.removeReplica(directory, sessionID)
    evictData(directory, [sessionID])
  }

  const seed = (
    directory: string,
    session: Session,
    options?: { isNew?: boolean; provisional?: boolean; messagesPending?: boolean },
  ) => {
    lifecycle.restore(directory, session.id)
    let inserted = false
    batch(() => {
      // An explicit install brings a removed chat back; a newer copy the store
      // already holds stays.
      input.set("session_removed", session.id, undefined)
      input.set("session", (sessions: Session[]) => {
        const result = Binary.search(sessions, session.id, (item) => item.id)
        const next = [...sessions]
        if (result.found) {
          if (admitsSession(input.data, next[result.index], session)) next[result.index] = session
        } else {
          inserted = true
          next.splice(result.index, 0, session)
        }
        return next
      })
      input.onSessionSeed?.(directory, session, {
        isNew: options?.isNew === true && inserted,
      })
      transcript.seed(directory, session.id, options)
      if (options?.provisional) provisional.add(session.id)
      else provisional.settle(session.id, true)
    })
  }

  const installActivatedSession = (
    directory: string,
    session: Session,
    snapshot: { items: Array<{ info: Message; parts: Part[] }>; complete: boolean; cursor?: string },
  ) => {
    batch(() => {
      // Confirming the chat ends its activation; this response keeps the fence.
      const activation = transcript.takeActivation(directory, session.id)
      // A model chosen while the chat was provisional is still being saved.
      seed(directory, updateModel.preservePendingSelection(session), { isNew: true })
      transcript.installServerSnapshot(directory, session.id, snapshot, activation)
    })
  }

  return {
    markMissing,
    retain: lifecycle.retain,
    retained: lifecycle.retained,
    retainedSessions: lifecycle.retainedSessions,
    /** The chats this page holds or shows, which the event stream must watch. */
    trackedSessions: lifecycle.trackedSessions,
    watchSince: transcript.watchSince,
    isMissing: lifecycle.isMissing,
    messagesReady: transcript.messagesReady,
    messagesAuthoritative: transcript.messagesAuthoritative,
    messagesCurrent: transcript.messagesCurrent,
    isProvisional: provisional.has,
    /** Resolves true once the server owns the session, false if it was discarded. */
    ready: provisional.wait,
    loadedTimestamp: transcript.loadedTimestamp,
    attemptedTimestamp: transcript.attemptedTimestamp,
    setAttempted: transcript.setAttempted,
    get: getSession,
    updateModel,
    preservePendingModelSelection: updateModel.preservePendingSelection,
    seed,
    installActivatedSession,
    installProvisionalTranscript: transcript.installProvisional,
    installServerTranscript: transcript.installServerSnapshot,
    resetTranscript: transcript.reset,
    reconcileTranscriptResets: transcript.reconcileResets,
    confirm(sessionID: string) {
      provisional.settle(sessionID, true)
    },
    discard(directory: string, sessionID: string) {
      provisional.settle(sessionID, false)
      input.set("session", (sessions: Session[]) => sessions.filter((session) => session.id !== sessionID))
      input.set("session_status", sessionID, undefined)
      input.set("session_waiting", sessionID, undefined)
      lifecycle.remove(directory, sessionID)
      evictData(directory, [sessionID])
    },
    optimistic: {
      ...optimistic.optimistic,
      /** Whether the transcript holds this message only as this device's unconfirmed copy. */
      has: optimistic.has,
    },
    addOptimisticMessage(
      directory: string,
      update: {
        sessionID: string
        messageID: string
        parts: Part[]
        agent: string
        model: { providerID: string; modelID: string }
        variant?: string
      },
    ) {
      optimistic.optimistic.add({
        directory,
        sessionID: update.sessionID,
        message: {
          id: update.messageID,
          sessionID: update.sessionID,
          role: "user",
          time: { created: Date.now() },
          agent: update.agent,
          model: update.model,
          variant: update.variant,
        },
        parts: update.parts,
      })
    },
    recovering: transcript.recovering,
    recoverText: transcript.recoverText,
    markStale: transcript.markStale,
    currentSince: transcript.currentSince,
    reconcileLatest: transcript.reconcileLatest,
    restore(directory: string, sessionID: string) {
      return transcript.restore(directory, sessionID)
    },
    async sync(
      directory: string,
      sessionID: string,
      options?: { force?: boolean; initialLimit?: number },
    ) {
      const wasProvisional = provisional.has(sessionID)
      if (!(await provisional.wait(sessionID))) return
      if (lifecycle.isMissing(directory, sessionID)) {
        // Fail as the request that found it gone did, so a caller that asks
        // after that request settled still learns the chat is gone.
        if (!options?.force) throw sessionNotFoundError()
        lifecycle.restore(directory, sessionID)
      }
      if (wasProvisional) transcript.markStale(directory, [sessionID])
      return transcript.sync(directory, sessionID, options)
    },
    diff: auxiliary.diff,
    persistEvent: transcript.persistEvent,
    history: transcript.history,
    invalidate(directory: string, sessionIDs: string[]) {
      optimistic.clear(directory, sessionIDs)
      transcript.clear(directory, sessionIDs)
    },
    evict(directory: string, sessionID: string) {
      if (lifecycle.retained(directory, sessionID)) return
      lifecycle.remove(directory, sessionID)
      evictData(directory, [sessionID])
    },
    clearDirectory(directory: string) {
      prefetch.clearDirectory(directory)
      lifecycle.clearDirectory(directory)
    },
    async load(directory: string, count = 10, limit: number) {
      const client = input.clientFor(directory)
      const result = await retry(() => client.session.list())
      const sessions = newestSessions(input.data, (result.data ?? []).filter((session) => !!session?.id)).sort((a, b) => cmp(a.id, b.id))
      const next = sessions.slice(0, limit + count)
      const keep = new Set(next.map((session) => session.id))
      const stale = input.data.session
        .filter((session) => session.workspaceID === directory && !session.parentID && !keep.has(session.id) && !lifecycle.retained(directory, session.id))
        .map((session) => session.id)
      for (const sessionID of stale) lifecycle.remove(directory, sessionID)
      evictData(directory, stale)
      input.set("session", (current: Session[]) => [
        ...current.filter((session) => session.workspaceID !== directory || (!keep.has(session.id) && lifecycle.retained(directory, session.id))),
        ...next,
      ])
    },
    dispose() {
      provisional.dispose()
      lifecycle.dispose()
      transcript.dispose()
      transcriptReplica.dispose()
      auxiliary.dispose()
      optimistic.dispose()
      prefetch.dispose()
      updateModel.dispose()
    },
  }
}
